import { logAutopilotPayload } from '../../utils/autopilot_debug';

// Request timeouts for the AI endpoints.
//
// The binding constraint is this client timeout, not the server. The chain is:
//   client $http timeout  <  services_controller Net::HTTP read_timeout (500s
//   PER CALL)  <  puma worker_timeout (3600s in development).
//
// extract_deal_terms and propose_document_changes each make SEVERAL sequential
// Azure calls — the extraction/proposal pass plus run_extraction_critic_pass —
// so total server time can legitimately exceed any single 500s read_timeout.
// The old 300s ceiling aborted the request client-side while Azure was still
// returning a valid response, which reads as a hang rather than a timeout.
//
// Overridable, highest precedence first, and resolved PER REQUEST so a change
// takes effect without a rebuild or a reload:
//   1. URL query param   ?ai_timeout_ms=2700000   (survives a reload — use this
//      when testing a large lease, since the editor reloads on save)
//   2. window global     window.LP_AI_REQUEST_TIMEOUT_MS = 45 * 60 * 1000
//   3. the default below
function timeoutFromEnvironment(paramName, globalName, fallbackMs) {
  if (typeof window === 'undefined') return fallbackMs;

  try {
    var fromQuery = new URLSearchParams(window.location.search).get(paramName);
    if (fromQuery !== null) {
      var parsed = parseInt(fromQuery, 10);
      // Ignore junk rather than sending timeout: NaN, which Angular treats as
      // no timeout at all — a typo in the URL should not silently disable it.
      if (isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch (e) {
    // URLSearchParams is unavailable or location is opaque; fall through.
  }

  var fromGlobal = window[globalName];
  if (
    typeof fromGlobal === 'number' &&
    isFinite(fromGlobal) &&
    fromGlobal > 0
  ) {
    return fromGlobal;
  }

  return fallbackMs;
}

function aiRequestTimeoutMs() {
  return timeoutFromEnvironment(
    'ai_timeout_ms',
    'LP_AI_REQUEST_TIMEOUT_MS',
    20 * 60 * 1000,
  );
}

// Document text extraction is a single Azure Content Understanding call, but a
// large or scanned PDF is slow; 120s was tight enough to fail on real uploads.
function documentExtractionTimeoutMs() {
  return timeoutFromEnvironment(
    'extraction_timeout_ms',
    'LP_EXTRACTION_TIMEOUT_MS',
    10 * 60 * 1000,
  );
}

angular.module('LeasePilot').service('ApiService', [
  '$http',
  function($http) {
    function convert(file, path) {
      var type =
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      var formData = new FormData();
      formData.append('file', file);
      return $http.post('/api/services/' + path, formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'arraybuffer',
      });
    }

    function getJson(file, path) {
      var formData = new FormData();
      formData.append('file', file);
      return $http.post('/api/services/' + path, formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: 'html/text' },
      });
    }

    function downloadFile(filename, type, data) {
      var a = document.createElement('a');
      var file = new Blob([data], {
        type: type,
      });
      var url = URL.createObjectURL(file);
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
    }

    function zipFiles(files, callback) {
      var zip = new JSZip();

      files.forEach(file => {
        zip.file(file.name, file.file);
      });

      return zip.generateAsync({ type: 'blob' });
    }

    var Base64 = {
      // private property
      _keyStr:
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',

      // public method for encoding
      encode: function(input) {
        var output = '';
        var chr1, chr2, chr3, enc1, enc2, enc3, enc4;
        var i = 0;

        input = Base64._utf8_encode(input);

        while (i < input.length) {
          chr1 = input.charCodeAt(i++);
          chr2 = input.charCodeAt(i++);
          chr3 = input.charCodeAt(i++);

          enc1 = chr1 >> 2;
          enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
          enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
          enc4 = chr3 & 63;

          if (isNaN(chr2)) {
            enc3 = enc4 = 64;
          } else if (isNaN(chr3)) {
            enc4 = 64;
          }

          output =
            output +
            this._keyStr.charAt(enc1) +
            this._keyStr.charAt(enc2) +
            this._keyStr.charAt(enc3) +
            this._keyStr.charAt(enc4);
        } // Whend

        return output;
      }, // End Function encode

      // private method for UTF-8 encoding
      _utf8_encode: function(string) {
        var utftext = '';
        string = string.replace(/\r\n/g, '\n');

        for (var n = 0; n < string.length; n++) {
          var c = string.charCodeAt(n);

          if (c < 128) {
            utftext += String.fromCharCode(c);
          } else if (c > 127 && c < 2048) {
            utftext += String.fromCharCode((c >> 6) | 192);
            utftext += String.fromCharCode((c & 63) | 128);
          } else {
            utftext += String.fromCharCode((c >> 12) | 224);
            utftext += String.fromCharCode(((c >> 6) & 63) | 128);
            utftext += String.fromCharCode((c & 63) | 128);
          }
        } // Next n

        return utftext;
      }, // End Function _utf8_encode
    };

    function import_(file, path, params) {
      var protocol = location.protocol === 'https:' ? 'https' : 'http';
      var type =
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      var formData = new FormData();
      formData.append('file', file);
      for (var key in params) {
        formData.append(key, params[key]);
      }
      var url = window._importer_service_url;
      return $http.post(protocol + '://' + url + path, formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'arraybuffer',
      });
    }

    function zipToRedline(file) {
      return convert(file, 'zip_to_redline');
    }

    async function htmlToWord(file, desiredStylesUrl) {
      let request = new XMLHttpRequest();

      return new Promise(function(resolve, reject) {
        request.onload = function() {
          const filesToZip = [
            { name: 'document.html', file },
            { name: 'style.docx', file: request.response },
          ];

          zipFiles(filesToZip).then(function(zipFile) {
            if (window.isDebug) {
              downloadFile('download_document.zip', 'application/zip', zipFile);
            }
            resolve(convert(zipFile, 'html_to_word'));
          });
        };

        request.open('GET', desiredStylesUrl, true);
        request.responseType = 'blob';
        request.send();
      });
    }

    async function htmlToClausebook(file, desiredStylesUrl) {
      let request = new XMLHttpRequest();

      return new Promise(function(resolve, reject) {
        request.onload = function() {
          const filesToZip = [
            { name: 'document.html', file },
            { name: 'style.docx', file: request.response },
          ];

          zipFiles(filesToZip).then(function(zipFile) {
            if (window.isDebug) {
              downloadFile('download_document.zip', 'application/zip', zipFile);
            }
            resolve(getJson(zipFile, 'html_to_clausebook'));
          });
        };

        request.open('GET', desiredStylesUrl, true);
        request.responseType = 'blob';
        request.send();
      });
    }

    function htmlToPdf(file, desiredStylesUrl) {
      let request = new XMLHttpRequest();

      return new Promise(function(resolve, reject) {
        request.onload = function() {
          const filesToZip = [
            {
              name: 'document.html',
              file,
            },
            {
              name: 'style.docx',
              file: request.response,
            },
          ];

          zipFiles(filesToZip).then(function(zipFile) {
            resolve(convert(zipFile, 'html_to_pdf'));
          });
        };

        request.open('GET', desiredStylesUrl, true);
        request.responseType = 'blob';
        request.send();
      });
    }

    function documentToPdf(file) {
      return convert(file, 'document_to_pdf');
    }

    function documentToHTML(file) {
      const path = 'document_to_html';
      const formData = new FormData();
      formData.append('file', file);
      return $http.post('/api/services/' + path, formData, {
        transformRequest: angular.identity,
        headers: {
          'Content-Type': undefined,
          Accept:
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });
    }

    function documentToText(file) {
      const path = 'document_to_text';
      const formData = new FormData();
      formData.append('file', file);
      return $http.post('/api/services/' + path, formData, {
        transformRequest: angular.identity,
        headers: {
          'Content-Type': undefined,
          Accept:
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        },
      });
    }

    function SMLToMSWord(file, formName) {
      return import_(file, '', {
        'add-free-texts': 'on',
        'download-word': 'on',
        'form-name': formName,
        form: 'sml',
      });
    }

    function SMLToMSWordWithChanges(file, changes) {
      console.log(file, {
        form: 'changes',
        changes: Base64.encode(JSON.stringify(changes)),
      });
      return import_(file, '', {
        form: 'changes',
        changes: Base64.encode(JSON.stringify(changes)),
      });
    }

    function diffgram(original, modified, desiredStylesUrl) {
      let request = new XMLHttpRequest();

      return new Promise(function(resolve, reject) {
        request.onload = function() {
          const filesToZip = [
            {
              name: 'original.html',
              file: original,
            },
            {
              name: 'modified.docx',
              file: modified,
            },
            {
              name: 'style.docx',
              file: request.response,
            },
          ];

          zipFiles(filesToZip).then(function(zipFile) {
            // remove comment to download zip file
            if (window.isDebug) {
              downloadFile('diff.zip', 'application/zip', zipFile);
            }
            resolve(getJson(zipFile, 'diffgram'));
          });
        };

        request.open('GET', desiredStylesUrl, true);
        request.responseType = 'blob';
        request.send();
      });
    }

    function redlineToHTML(file) {
      return convert(file, 'redline_to_html');
    }

    function rtfToHtml(data) {
      const type = 'application/text';
      const formData = new FormData();

      formData.append('rtf', data);

      return $http.post('/api/services/rtf_to_html', formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'arraybuffer',
      });
    }

    function snippet(data) {
      var protocol = location.protocol === 'https:' ? 'https' : 'http';
      var type = 'application/text';
      var formData = new FormData();
      formData.append('form', 'snippet');
      formData.append('snippet', data);
      var url = window._importer_service_url;
      return $http.post(protocol + '://' + url, formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'arraybuffer',
      });
    }

    function abstract({
      terms,
      doc,
      instructions = '',
      format = '',
      documentType = '',
      documentFlavor = '',
    }) {
      if (!doc) {
        throw new Error('Document is required');
      }

      const type = 'application/text';
      const formData = new FormData();

      formData.append('terms', JSON.stringify(terms));
      formData.append('document', doc);
      formData.append('instructions', instructions);
      formData.append('documentFormat', format);
      formData.append('documentType', documentType);
      formData.append('documentFlavor', documentFlavor);

      return $http.post('/api/services/abstract', formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'plain/text',
        timeout: aiRequestTimeoutMs(),
      });
    }

    function saveAbstract({ documentId, json, docx }) {
      const type = 'application/text';
      const formData = new FormData();

      formData.append('documentId', documentId);

      if (json) {
        formData.append('json', json);
      }

      if (docx) {
        formData.append('docx', docx);
      }

      return $http.post('/api/services/save_abstract', formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        responseType: 'plain/text',
      });
    }

    function getChangelog() {
      return $http.get('/api/changelog');
    }

    function extractDocumentText({ documentId, files, text }) {
      const type = 'application/json';
      const formData = new FormData();

      formData.append('documentId', documentId);

      // Append files if provided
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i]);
        }
      }

      // Append text if provided
      if (text) {
        formData.append('text', text);
      }

      return $http.post('/api/services/extract_document_text', formData, {
        transformRequest: angular.identity,
        headers: { 'Content-Type': undefined, Accept: type },
        timeout: documentExtractionTimeoutMs(),
      });
    }

    // One redline, and optionally the executed version of the same deal. The
    // pair is what lets the classifier tell a concession from a refusal — with
    // the redline alone every change reads as "the counterparty asked", and
    // nothing records whether they got it.
    //
    // Deliberately one pair per request, because that is what the endpoint
    // takes: it converts the document, diffs it, and classifies every tracked
    // change against the vocabulary in a single synchronous pass.
    function importPastRedline({ documentType, redline, executed }) {
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('redline', redline);
      if (executed) formData.append('executed', executed);

      // Returns 202 as soon as the documents are stored and the row is queued.
      // The reading happens in a worker, so this waits on an upload rather than
      // on two dozen model calls over a whole lease.
      return $http.post('/api/services/import_past_redline', formData, {
        transformRequest: angular.identity,
        headers: {
          'Content-Type': undefined,
          Accept: 'application/json',
        },
        timeout: aiRequestTimeoutMs(),
      });
    }

    // What the screen polls while a worker reads. Cheap and idempotent — it
    // reports the rows' own counts and never the documents' contents.
    function listPastRedlineImports(documentType) {
      return $http.get('/api/services/past_redline_imports', {
        params: { documentType: documentType },
        headers: { Accept: 'application/json' },
      });
    }

    // The outline is produced in a browser, because section numbering exists
    // only in a rendered form. This posts what that render projected.
    function importFormPositions({ formId, documentType, outline }) {
      return $http.post(
        '/api/services/import_form_positions',
        { formId: formId, documentType: documentType, outline: outline },
        { headers: { Accept: 'application/json' } },
      );
    }

    function listFormPositionImports(documentType) {
      return $http.get('/api/services/form_position_imports', {
        params: { documentType: documentType },
        headers: { Accept: 'application/json' },
      });
    }

    // lp-015. The document's own figures, read against the playbook's ladders.
    // Sections rather than the flat projection: the reader cites the block a
    // quote came from, and a projection has no blocks to cite.
    function checkDocument({ documentId, documentType, sections }) {
      return $http.post(
        '/api/services/check_document',
        // lp-017. The id so the server can keep this outline, which is the only
        // moment it ever sees what a lease actually says — the projection lives
        // in the browser — and therefore the only way a dry run gets a document.
        { documentId: documentId, documentType: documentType, sections: sections },
        { headers: { Accept: 'application/json' } },
      );
    }

    // lp-016 §5. Decisions about findings, for everyone on the lease rather
    // than for one browser.
    function listCheckDecisions(documentId) {
      return $http.get('/api/check_decisions', {
        params: { documentId: documentId },
        headers: { Accept: 'application/json' },
      });
    }

    function recordCheckDecision(documentId, decision) {
      return $http.post(
        '/api/check_decisions',
        { documentId: documentId, ...decision },
        { headers: { Accept: 'application/json' } },
      );
    }

    function clearCheckDecision(documentId, findingId) {
      return $http.delete('/api/check_decisions/clear', {
        params: { documentId: documentId, findingId: findingId },
        headers: { Accept: 'application/json' },
      });
    }

    function importPlaybook({ documentType, files }) {
      const formData = new FormData();
      formData.append('documentType', documentType);

      for (let index = 0; index < files.length; index++) {
        formData.append('files[]', files[index]);
      }

      return $http.post('/api/services/import_playbook', formData, {
        transformRequest: angular.identity,
        headers: {
          'Content-Type': undefined,
          Accept: 'application/json',
        },
        // Extraction and classification run sequentially in this request.
        timeout: aiRequestTimeoutMs(),
      });
    }

    function getPlaybook(documentType) {
      return $http.get('/api/playbooks', {
        params: documentType ? { documentType: documentType } : {},
        headers: { Accept: 'application/json' },
      });
    }

    // Bytes, not JSON. `arraybuffer` rather than a plain GET so the button can
    // show progress and an error can be reported in the editor — a bare
    // navigation to the URL downloads the file but hands a failure to the
    // browser, which renders our JSON error as a page.
    function downloadPlaybookDocument({ documentType, template, includeHistory }) {
      return $http.get('/api/playbooks/document', {
        params: {
          documentType: documentType,
          template: template ? 'true' : 'false',
          includeHistory: includeHistory ? 'true' : 'false',
        },
        responseType: 'arraybuffer',
      });
    }

    // lp-017. The customer's own checklist. Reviewing is the slow one — it is a
    // model pass over the wording — so it borrows the AI timeout rather than
    // the default, which would give up on a long list mid-answer.
    function getChecklist(documentType) {
      return $http.get('/api/checklists', {
        params: documentType ? { documentType: documentType } : {},
        headers: { Accept: 'application/json' },
      });
    }

    function addChecklistItem({ documentType, text, severity, scopeHint, enabled }) {
      return $http.post(
        '/api/checklists',
        {
          documentType: documentType,
          text: text,
          severity: severity,
          scopeHint: scopeHint,
          enabled: enabled,
        },
        { headers: { Accept: 'application/json' } },
      );
    }

    function updateChecklistItem(id, changes) {
      return $http.patch(`/api/checklists/${id}`, changes, {
        headers: { Accept: 'application/json' },
      });
    }

    function deleteChecklistItem(id) {
      return $http.delete(`/api/checklists/${id}`, {
        headers: { Accept: 'application/json' },
      });
    }

    // lp-017. Out to Word and back, as a pair. A template when there is nothing
    // to export yet, which is the same document with the items stripped and the
    // guidance added.
    function downloadChecklistDocument({ documentType, template }) {
      return $http.get('/api/checklists/document', {
        params: {
          documentType: documentType,
          template: template ? 'true' : 'false',
        },
        responseType: 'arraybuffer',
      });
    }

    function importChecklistDocument({ documentType, file }) {
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('files[]', file);

      return $http.post('/api/checklists/import', formData, {
        headers: { 'Content-Type': undefined, Accept: 'application/json' },
        transformRequest: angular.identity,
        timeout: aiRequestTimeoutMs(),
      });
    }

    // lp-017. The documents a dry run may be pointed at, and the dry run.
    function checklistDocuments() {
      return $http.get('/api/checklists/documents', {
        headers: { Accept: 'application/json' },
      });
    }

    function dryRunChecklist({ documentType, documentId }) {
      return $http.post(
        '/api/checklists/dry_run',
        { documentType: documentType, documentId: documentId },
        { headers: { Accept: 'application/json' }, timeout: aiRequestTimeoutMs() },
      );
    }

    function acceptChecklistSuggestion(id) {
      return $http.post(
        `/api/checklists/${id}/accept_suggestion`,
        {},
        { headers: { Accept: 'application/json' } },
      );
    }

    function reviewChecklist(documentType) {
      return $http.post(
        '/api/checklists/review',
        { documentType: documentType },
        {
          headers: { Accept: 'application/json' },
          timeout: aiRequestTimeoutMs(),
        },
      );
    }

    function commitPlaybook({ documentType, entries }) {
      return $http.post(
        '/api/playbooks',
        { documentType: documentType, entries: entries },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      );
    }

    function extractDealTerms({
      documentId,
      text,
      instructions,
      schema,
      fieldConstraints,
    }) {
      const type = 'application/json';
      const payload = {
        documentId: documentId,
        text: text,
        fieldConstraints: fieldConstraints || [],
      };

      if (instructions) {
        payload.instructions = instructions;
      }

      if (schema) {
        payload.schema = schema;
      }

      return $http.post('/api/services/extract_deal_terms', payload, {
        headers: { 'Content-Type': 'application/json', Accept: type },
        // Extraction + confidence critic can exceed 120s on large documents.
        timeout: aiRequestTimeoutMs(),
      });
    }

    function trackDealTermsImportOutcome(payload) {
      const type = 'application/json';
      return $http.post(
        '/api/services/track_deal_terms_import_outcome',
        payload || {},
        {
          headers: { 'Content-Type': 'application/json', Accept: type },
        },
      );
    }

    function recordCounterpartyDecision(payload) {
      const type = 'application/json';
      return $http.post('/api/services/record_counterparty_decision', payload, {
        headers: { 'Content-Type': 'application/json', Accept: type },
      });
    }

    // Names the terms the source document negotiates. Fired the moment phase
    // one lands, while the drafter is still reviewing the highlighted fields,
    // so phase two starts with the list already in hand and pays nothing for
    // it. Never rejects into the caller's path: without a list phase two reads
    // the document with one reader, which is what it did before.
    function listSourceConcepts({ documentId, text }) {
      return $http
        .post(
          '/api/services/list_source_concepts',
          { documentId: documentId, text: text },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            timeout: aiRequestTimeoutMs(),
          },
        )
        .then(function(response) {
          return (response.data && response.data.concepts) || [];
        })
        .catch(function(error) {
          console.warn(
            '[listSourceConcepts] falling back to one reader',
            error,
          );
          return [];
        });
    }

    function proposeDocumentChanges({
      documentId,
      projection,
      sourceText,
      notes,
      fieldConstraints,
      concepts,
    }) {
      return $http.post(
        '/api/services/propose_document_changes',
        {
          documentId: documentId,
          projection: projection,
          sourceText: sourceText,
          notes: notes || [],
          // The same list phase one extracts against, for the opposite purpose:
          // phase two uses it to know which terms it must not draft language
          // for, because the form already owns them.
          fieldConstraints: fieldConstraints || [],
          // Decides how the lease is read: one reader per share of these. An
          // empty list is not an error, it is one reader over the whole thing.
          concepts: concepts || [],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          timeout: aiRequestTimeoutMs(),
        },
      );
    }

    function clausebookCandidates({ documentId, concept, proposal }) {
      return $http.post(
        '/api/services/clausebook_candidates',
        {
          documentId: documentId,
          concept: concept,
          conceptId: concept && concept.id,
          proposal: proposal,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          timeout: aiRequestTimeoutMs(),
        },
      );
    }

    function clausebookBodies({
      documentId,
      retrievalToken,
      candidatePositions,
    }) {
      return $http.post(
        '/api/services/clausebook_bodies',
        {
          documentId: documentId,
          retrievalToken: retrievalToken,
          candidatePositions: candidatePositions,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          timeout: aiRequestTimeoutMs(),
        },
      );
    }

    function recordClausebookRetrieval({
      documentId,
      retrievalToken,
      selected,
      reason,
    }) {
      return $http.post(
        '/api/services/record_clausebook_retrieval',
        {
          documentId: documentId,
          retrievalToken: retrievalToken,
          selected: selected,
          reason: reason,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      );
    }

    function clausebookGaps({ documentId }) {
      return $http.post(
        '/api/services/clausebook_gaps',
        { documentId: documentId },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      );
    }

    function reviewCounterpartyComments({
      documentId,
      projection,
      documentType,
    }) {
      return $http
        .post(
          '/api/services/review_counterparty_comments',
          {
            documentId: documentId,
            projection: projection,
            documentType: documentType,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            timeout: aiRequestTimeoutMs(),
          },
        )
        .then(function(response) {
          logAutopilotPayload('counterpartyComments', response.data);
          return response;
        });
    }

    function acceptAiServicesConsent(version) {
      const type = 'application/json';
      return $http.put(
        '/api/users/accept_ai_services_consent',
        {
          version: version,
        },
        {
          headers: { 'Content-Type': 'application/json', Accept: type },
        },
      );
    }

    return {
      convert: convert,
      htmlToWord: htmlToWord,
      zipToRedline: zipToRedline,
      htmlToPdf: htmlToPdf,
      SMLToMSWord: SMLToMSWord,
      SMLToMSWordWithChanges: SMLToMSWordWithChanges,
      documentToPdf: documentToPdf,
      documentToHTML: documentToHTML,
      documentToText: documentToText,
      diffgram: diffgram,
      redlineToHTML: redlineToHTML,
      snippet: snippet,
      rtfToHtml: rtfToHtml,
      htmlToClausebook: htmlToClausebook,
      abstract: abstract,
      saveAbstract: saveAbstract,
      getChangelog: getChangelog,
      extractDocumentText: extractDocumentText,
      checkDocument: checkDocument,
      listCheckDecisions: listCheckDecisions,
      recordCheckDecision: recordCheckDecision,
      clearCheckDecision: clearCheckDecision,
      importPlaybook: importPlaybook,
      importPastRedline: importPastRedline,
      listPastRedlineImports: listPastRedlineImports,
      importFormPositions: importFormPositions,
      listFormPositionImports: listFormPositionImports,
      getPlaybook: getPlaybook,
      getChecklist: getChecklist,
      addChecklistItem: addChecklistItem,
      updateChecklistItem: updateChecklistItem,
      deleteChecklistItem: deleteChecklistItem,
      acceptChecklistSuggestion: acceptChecklistSuggestion,
      checklistDocuments: checklistDocuments,
      downloadChecklistDocument: downloadChecklistDocument,
      importChecklistDocument: importChecklistDocument,
      dryRunChecklist: dryRunChecklist,
      reviewChecklist: reviewChecklist,
      downloadPlaybookDocument: downloadPlaybookDocument,
      downloadFile: downloadFile,
      commitPlaybook: commitPlaybook,
      extractDealTerms: extractDealTerms,
      listSourceConcepts: listSourceConcepts,
      proposeDocumentChanges: proposeDocumentChanges,
      clausebookCandidates: clausebookCandidates,
      clausebookBodies: clausebookBodies,
      clausebookGaps: clausebookGaps,
      recordClausebookRetrieval: recordClausebookRetrieval,
      reviewCounterpartyComments: reviewCounterpartyComments,
      trackDealTermsImportOutcome: trackDealTermsImportOutcome,
      recordCounterpartyDecision: recordCounterpartyDecision,
      acceptAiServicesConsent: acceptAiServicesConsent,
    };
  },
]);
